/**
 * Список партнёров реферальной программы — /partners.
 *
 * Шапка: 5 KPI-карточек (всего партнёров, всего привлечено, активировано,
 * оплатили, общая выручка).
 *
 * Таблица: имя партнёра, привёл / активировал / оплатил, конверсии (%),
 * выручка, дата последнего приглашения. Кликабельная — ведёт на /partners/{tg_id}.
 *
 * Фильтры: search (имя/username/tg_id), min_invited, has_paid (только активные).
 * Сортировка по любой числовой колонке (server-side).
 */
import { useState, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import { API_URL } from "../../dataProvider";
import {
  Table, Tag, Typography, Card, Row, Col, Statistic, Space, Input, Select,
  Tooltip, InputNumber,
} from "antd";
import {
  TeamOutlined, UserAddOutlined, CheckCircleOutlined,
  CreditCardOutlined, DollarOutlined, SearchOutlined, ClearOutlined,
} from "@ant-design/icons";
import { Link } from "react-router";
import { fmtDateTime } from "../../utils";

interface Partner {
  tg_id: number;
  first_name: string | null;
  last_name: string | null;
  tg_username: string | null;
  invited: number;
  activated: number;
  paid: number;
  revenue_rub: number;
  last_invite_at: string | null;
  activation_rate: number;
  paid_conversion_rate: number;
}

interface PartnersSummary {
  partners_total: number;
  invited_total: number;
  activated_total: number;
  paid_users_total: number;
  total_revenue_rub: number;
}

const fmtRub = (n: number): string => {
  if (n === 0) return "0 ₽";
  return new Intl.NumberFormat("ru-RU").format(Math.round(n)) + " ₽";
};

export const PartnersList = () => {
  // KPI-карточки: endpoint /admin/partners/summary возвращает ПЛОСКИЙ объект
  // {partners_total, invited_total, ...} — не list-формат с data[].
  // useList не подходит (ожидает массив), useCustom в этой сборке Refine v5
  // не возвращает данные (см. note в subscriptions/show.tsx — useCustomMutation
  // тоже глючит). Поэтому используем direct fetch + useState — самый
  // надёжный путь.
  const [summary, setSummary] = useState<PartnersSummary>({
    partners_total: 0, invited_total: 0, activated_total: 0,
    paid_users_total: 0, total_revenue_rub: 0,
  });
  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const r = await fetch(`${API_URL}/admin/partners/summary`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const j: PartnersSummary = await r.json();
        if (!cancelled) setSummary(j);
      } catch {
        // тихо — KPI просто останутся нулями, таблица всё равно работает
      }
    };
    fetchSummary();
    // Авто-обновление KPI каждые 60 секунд (юзеры регаются регулярно)
    const interval = setInterval(fetchSummary, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Основная таблица
  const { tableProps, filters, setFilters } = useTable<Partner>({
    resource: "partners",
    syncWithLocation: true,
    pagination: { mode: "server", pageSize: 50 },
    sorters: { initial: [{ field: "invited", order: "desc" }] },
  });

  // Debounced поиск
  const qF = filters.find(f => "field" in f && f.field === "q");
  const minF = filters.find(f => "field" in f && f.field === "min_invited");
  const paidF = filters.find(f => "field" in f && f.field === "has_paid");
  const [qInput, setQInput] = useState<string>(
    (qF && "value" in qF ? String(qF.value) : "") || ""
  );
  useEffect(() => {
    const t = setTimeout(() => {
      const cleaned = qInput.trim();
      const curr = (qF && "value" in qF ? String(qF.value) : "") || "";
      if (cleaned === curr) return;
      setFilters([
        ...filters.filter(f => "field" in f && f.field !== "q"),
        ...(cleaned ? [{ field: "q", operator: "eq" as const, value: cleaned }] : []),
      ], "replace");
    }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const hasActive = !!(qInput || minF || paidF);
  const clearAll = () => {
    setQInput("");
    setFilters([], "replace");
  };

  return (
    <List title="Партнёрская программа" breadcrumb={false}>
      {/* KPI карточки */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card size="small">
            <Statistic title="Партнёров" value={summary.partners_total}
              prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card size="small">
            <Statistic title="Привлекли всего" value={summary.invited_total}
              prefix={<UserAddOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card size="small" style={{ borderColor: "#52c41a33" }}>
            <Statistic title="Активировали бот" value={summary.activated_total}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={5}>
          <Card size="small" style={{ borderColor: "#1677ff33" }}>
            <Statistic title="Оплатили" value={summary.paid_users_total}
              prefix={<CreditCardOutlined style={{ color: "#1677ff" }} />}
              valueStyle={{ color: "#1677ff" }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" style={{ borderColor: "#faad1433" }}>
            <Statistic title="Выручка" value={fmtRub(summary.total_revenue_rub)}
              prefix={<DollarOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14", fontSize: 18 }} />
          </Card>
        </Col>
      </Row>

      {/* Фильтры */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          placeholder="Поиск: имя, @username, tg_id…"
          value={qInput}
          onChange={e => setQInput(e.target.value)}
          style={{ width: 320 }}
        />
        <InputNumber
          placeholder="Привёл от N"
          min={1} max={1000}
          value={minF && "value" in minF ? Number(minF.value) : undefined}
          onChange={v => setFilters([
            ...filters.filter(f => "field" in f && f.field !== "min_invited"),
            ...(v ? [{ field: "min_invited", operator: "eq" as const, value: String(v) }] : []),
          ], "replace")}
          style={{ width: 160 }}
        />
        <Select
          style={{ width: 220 }} allowClear placeholder="Все"
          value={paidF && "value" in paidF ? String(paidF.value) : undefined}
          onChange={v => setFilters([
            ...filters.filter(f => "field" in f && f.field !== "has_paid"),
            ...(v ? [{ field: "has_paid", operator: "eq" as const, value: v }] : []),
          ], "replace")}
          options={[
            { value: "true", label: "💰 Только с оплатами" },
          ]}
        />
        {hasActive && (
          <Tooltip title="Сбросить все фильтры">
            <button onClick={clearAll} style={{
              padding: "4px 12px", borderRadius: 6, border: "1px solid #ff7875",
              background: "#fff", color: "#ff4d4f", cursor: "pointer",
            }}>
              <ClearOutlined /> Сбросить
            </button>
          </Tooltip>
        )}
      </Space>

      <Table<Partner> {...tableProps} rowKey="tg_id" size="small">
        <Table.Column<Partner> dataIndex="tg_id" title="TG ID" width={120}
          sorter
          render={(v: number, r) => (
            <Link to={`/partners/${v}`}>
              <Typography.Text code style={{ fontSize: 12 }}>{v}</Typography.Text>
            </Link>
          )} />
        <Table.Column<Partner> title="Партнёр"
          render={(_, r) => {
            const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || "—";
            return (
              <Space size={4}>
                <Link to={`/partners/${r.tg_id}`}>
                  <Typography.Text strong>{name}</Typography.Text>
                </Link>
                {r.tg_username && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    @{r.tg_username}
                  </Typography.Text>
                )}
              </Space>
            );
          }} />
        <Table.Column<Partner> dataIndex="invited" title="Привёл" width={100}
          sorter defaultSortOrder="descend"
          render={(v: number) => <Tag color="blue">{v}</Tag>} />
        <Table.Column<Partner> dataIndex="activated" title="Активировал" width={140}
          sorter
          render={(v: number, r) => (
            <Tooltip title={`${r.activation_rate}% от привлечённых`}>
              <Space size={4}>
                <Tag color="green">{v}</Tag>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {r.activation_rate}%
                </Typography.Text>
              </Space>
            </Tooltip>
          )} />
        <Table.Column<Partner> dataIndex="paid" title="Оплатил" width={130}
          sorter
          render={(v: number, r) => (
            <Tooltip title={`${r.paid_conversion_rate}% конверсия из активировавших`}>
              <Space size={4}>
                <Tag color={v > 0 ? "gold" : "default"}>{v}</Tag>
                {v > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {r.paid_conversion_rate}%
                  </Typography.Text>
                )}
              </Space>
            </Tooltip>
          )} />
        <Table.Column<Partner> dataIndex="revenue_rub" title="Выручка" width={120}
          sorter
          render={(v: number) =>
            v > 0
              ? <Typography.Text strong style={{ color: "#faad14" }}>{fmtRub(v)}</Typography.Text>
              : <Typography.Text type="secondary">—</Typography.Text>
          } />
        <Table.Column<Partner> dataIndex="last_invite_at" title="Последнее приглашение" width={170}
          sorter
          render={(v: string | null) =>
            v
              ? <Typography.Text style={{ fontSize: 12 }}>{fmtDateTime(v)}</Typography.Text>
              : <Typography.Text type="secondary">—</Typography.Text>
          } />
      </Table>
    </List>
  );
};
